import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  Building2, 
  MapPin, 
  Briefcase, 
  Calendar, 
  ChevronRight, 
  Send, 
  CheckCircle2, 
  XCircle,
  Clock,
  DollarSign,
  Rocket,
  CalendarDays,
  Layers,
  Zap,
  Sparkle,
  FileText
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { JobInvitationResponse, UpdateInvitationStatusRequest } from '../../InvitatationSystem/Types/InvitationSystemType';
import { updateInvitationStatus } from '../../InvitatationSystem/Services/InvitationService';
import ReactMarkdown from 'react-markdown';

interface JobInviteCardProps {
    data: JobInvitationResponse[];
}

const JobInvitationComponent: React.FC<JobInviteCardProps> = ({ data }) => {
  /* 
    FIX 1: Changed `isReqOpen` from a boolean to accept a specific invitation object or null. 
    This allows us to track exactly *which* job invitation details should be viewed.
  */
  const [selectedReqInvitation, setSelectedReqInvitation] = useState<JobInvitationResponse | null>(null);
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [response, setResponse] = useState({ status: 'ACCEPTED', message: '' });
  const [selectedInvitation, setSelectedInvitation] = useState<JobInvitationResponse | null>(null);

   const handleSubmit = async () => {
  try {
    if (!selectedInvitation) {
      alert('Please select an invitation first');
      return;
    }

    const payload: UpdateInvitationStatusRequest = {
      invitationId: selectedInvitation.invitationId,
      recruiterId: selectedInvitation.recruiterId,
      candidateId: selectedInvitation.candidateId,
      jobTemplateId: selectedInvitation.jobId,
      invitationStatus: response.status,
      message: response.message,
    };
    
   console.log("recruiterId ",selectedInvitation.recruiterId)

   const updatedResponse = await updateInvitationStatus(payload, selectedInvitation.invitationId);

    alert('Invitation '+response.status);
    setIsActionOpen(false);

  } catch (error) {
    console.error(error);
    alert('Failed to update invitation');
  }
};

return (
  <>
  {data.map((item, index) => ( /* Changed loop variable name to 'item' to avoid shadowing global variables or confusion */
  <div  key={index} className="w-full max-w-5xl mx-auto mb-3 px-2 md:px-4 font-sans mt-7">
    {/* --- Main Horizontal Card --- */}
    {/* --- Premium Job Card --- */}

<motion.div
initial={{ opacity: 0, y: 12 }}
animate={{ opacity: 1, y: 0 }}
whileHover={{ y: -3, transition: { duration: 0.2 } }}
className="relative group bg-slate-950 border border-slate-800 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] hover:shadow-[0_20px_40px_rgba(34,211,238,0.08)] hover:border-cyan-500/50 transition-all p-4 md:p-5 flex flex-col gap-4 overflow-hidden"
>
{/* TOP SECTION: Logo, Title, and Status Box */}
<div className="flex flex-col sm:flex-row items-center justify-between gap-4 relative  z-10">
  <div className="flex items-center w-full sm:w-auto gap-4">
    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-slate-900 border-2 border-slate-800 p-2 flex-shrink-0 relative shadow-inner ring-1 ring-cyan-500/20">
      <img
        src={item.companyLogoUrl || "/api/placeholder/80/80"}
        alt={item.companyName}
        className="w-full h-full object-contain rounded-lg brightness-90 invert-0"
      />
      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-slate-950 flex items-center justify-center ${item.jobStatus ? 'bg-emerald-400' : 'bg-slate-600'}`}>
        {item.jobStatus && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
      </div>
    </div>

    {/* Identity & Core Specs */}
    <div className="flex-1 min-w-0">
      

      <div className="flex flex-col gap-1 mb-1">
<div className="flex items-center gap-1.5 uppercase text-[9px] font-black tracking-[0.15em] text-cyan-400">
  <Sparkle size={10} className="animate-pulse" />
  New Opportunity
</div>

<div className="flex flex-wrap items-center gap-3">
  <h3 className="text-base md:text-xl font-black text-slate-100 tracking-tight leading-none">
    {item.jobTittle}
  </h3>

  <div className="group/level relative flex items-center gap-2 px-2.5 py-1 bg-slate-900 border border-slate-800 rounded-lg shadow-sm hover:border-cyan-500/40 transition-colors cursor-help">
    <div className="flex gap-0.5 items-end h-3">
      <div className={`w-1 rounded-full bg-cyan-400 ${item.roleLevel ? 'h-full' : 'h-1/3'}`} />
      <div className={`w-1 rounded-full ${['INTERMEDIATE', 'SENIOR', 'LEAD', 'PRINCIPAL'].includes(item.roleLevel?.toUpperCase()) ? 'h-full bg-cyan-400' : 'h-1/2 bg-slate-700'}`} />
      <div className={`w-1 rounded-full ${['SENIOR', 'LEAD', 'PRINCIPAL'].includes(item.roleLevel?.toUpperCase()) ? 'h-full bg-cyan-400' : 'h-1/3 bg-slate-700'}`} />
    </div>
    
    <span className="text-[10px] font-black uppercase tracking-wider text-slate-300">
      {item.roleLevel}
    </span>

    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-950 text-slate-100 border border-slate-800 text-[8px] font-bold rounded opacity-0 group-hover/level:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
      Experience Tier: {item.roleLevel}
    </div>
  </div>
</div>
</div>
      
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-400 text-[10px] md:text-xs font-semibold">
        <a href={item.website} target="_blank" className="text-cyan-400 flex items-center gap-1.5 hover:underline decoration-cyan-400/50 decoration-2">
          <Building2 size={13} className="text-cyan-400" /> {item.companyName}
        </a>
        <span className="flex items-center gap-1.5 font-medium text-slate-400">
          <MapPin size={13} className="text-slate-500" /> {item.location}
        </span>
        <span className="bg-slate-800 text-slate-100 border border-slate-700 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest scale-90 origin-left">
          {item.workType}
        </span>
      </div>
    </div>
  </div>

  {/* CTA & Status Sidebar */}
  <div className="flex items-center justify-between w-full sm:w-auto sm:justify-end gap-3 border-t sm:border-t-0 border-slate-900 pt-3 sm:pt-0">
    <div className={`px-3 py-2 rounded-xl text-center min-w-[100px] border shadow-inner ${
        item.invitationStatus === 'PENDING' 
        ? 'bg-slate-900 border-emerald-500/30 text-emerald-400' 
        : 'bg-slate-900 border-cyan-500/30 text-cyan-400'
      }`}>
      <p className="text-[9px] font-bold text-slate-400 uppercase leading-none mb-1 tracking-wider">Invitation</p>
      <p className={`text-[11px] font-black uppercase tracking-tighter ${
        item.invitationStatus === 'PENDING' ? 'text-emerald-400' : 'text-cyan-400'
      }`}>
        {item.invitationStatus || 'PENDING'}
      </p>
    </div>
    
    <div className="flex items-center gap-2">
      <button
        /* FIX 2: Instead of passing true, we now pass the current loop iteration object data */
        onClick={() => setSelectedReqInvitation(item)}
        className="relative group px-5 py-2.5 text-[10px] font-black uppercase tracking-widest overflow-hidden rounded-full transition-all active:scale-95 border border-slate-800 bg-slate-900 shadow-[0_0_20px_rgba(0,0,0,0.2)] hover:shadow-[0_0_25px_rgba(34,211,238,0.1)]"
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900 via-slate-900 to-slate-800 group-hover:from-slate-850 group-hover:to-slate-750 transition-colors" />
        <span className="relative flex items-center gap-2 text-cyan-400 group-hover:text-cyan-300">
          <FileText size={14} className="stroke-[2.5]" />
          View Requirements
        </span>
      </button>
      
      <motion.button
        whileTap={{ scale: 0.96 }}
        onClick={() => {setSelectedInvitation(item); setIsActionOpen(true);}}
        className="px-6 py-2.5 text-[10px] font-black uppercase tracking-widest bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 rounded-full shadow-lg shadow-cyan-500/10 flex items-center gap-2.5"
      >
        <Zap size={14} className="fill-slate-950/30 text-slate-950" />
        Accept
      </motion.button>
    </div>
  </div>
</div>

{/* BOTTOM SECTION */}
<div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-900 relative z-10">
  <div className="flex flex-col gap-1.5 p-3 rounded-xl border border-slate-800 bg-slate-900/50">
    <div className="flex items-center gap-1.5">
      <Layers className="w-3.5 h-3.5 text-cyan-400" />
      <p className="text-[9px] uppercase font-extrabold text-slate-400 tracking-wider">Experience</p>
    </div>
    <p className="text-xs font-bold text-slate-200">{item.miniExperience}—{item.maxExperience} Years</p>
  </div>
  
  <div className="flex flex-col gap-1.5 p-3 rounded-xl border border-amber-500/20 bg-amber-500/5">
    <div className="flex items-center gap-1.5">
      <CalendarDays className="w-3.5 h-3.5 text-amber-400" />
      <p className="text-[9px] uppercase font-extrabold text-amber-400/70 tracking-wider">Notice Period</p>
    </div>
    <p className="text-xs font-bold text-amber-400">{item.noticePeriodDays} Days</p>
  </div>

  <div className="flex flex-col gap-1.5 p-3 rounded-xl border border-sky-500/20 bg-sky-50/5">
    <div className="flex items-center gap-1.5">
      <Building2 className="w-3.5 h-3.5 text-sky-400" />
      <p className="text-[9px] uppercase font-extrabold text-sky-400/70 tracking-wider">Industry</p>
    </div>
    <p className="text-xs font-bold text-slate-200 truncate">{item.industry}</p>
  </div>

  <div className="flex flex-col gap-1.5 p-3 rounded-xl border border-fuchsia-500/20 bg-fuchsia-50/5">
    <div className="flex items-center gap-1.5">
      <Rocket className="w-3.5 h-3.5 text-fuchsia-400" />
      <p className="text-[9px] uppercase font-extrabold text-fuchsia-400/70 tracking-wider">HQ Location</p>
    </div>
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-bold text-fuchsia-400 truncate">{item.headquartersLocation}</span>
    </div>
  </div>
</div>
</motion.div>




{/* --- MODAL 1: View Requirements --- */}
{/* FIX 3: We evaluate if `selectedReqInvitation` equals this iteration's item ID. This isolates each modal instance perfectly! */}
<Transition show={selectedReqInvitation?.invitationId === item.invitationId} as={React.Fragment}>
<Dialog as="div" className="relative z-50" onClose={() => setSelectedReqInvitation(null)}>
  <Transition.Child
    as={React.Fragment}
    enter="ease-out duration-300"
    enterFrom="opacity-0"
    enterTo="opacity-100"
    leave="ease-in duration-200"
    leaveFrom="opacity-100"
    leaveTo="opacity-0"
  >
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
  </Transition.Child>

  <div className="fixed inset-0 overflow-y-auto">
    <div className="flex min-h-full items-center justify-center p-4">
      <Transition.Child
        as={React.Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-slate-900 border border-slate-800 p-8 shadow-2xl transition-all">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <Dialog.Title as="h3" className="text-2xl font-black text-slate-100 tracking-tight leading-tight">
                {item.jobTittle}
              </Dialog.Title>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-cyan-400 font-bold uppercase tracking-wider text-xs">{item.jobSummarySlug}</p>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${item.jobStatus ? 'bg-emerald-400 text-slate-950 shadow-[0_0_10px_rgba(52,211,153,0.3)]' : 'bg-rose-400 text-slate-950 shadow-[0_0_10px_rgba(251,113,133,0.3)]'}`}>
                  {item.jobStatus ? 'Active' : 'Closed'}
                </span>
              </div>
            </div>
            {/* FIX 4: Changed close button action to trigger state clearance (null) */}
            <button onClick={() => setSelectedReqInvitation(null)} className="p-2 hover:bg-slate-800 rounded-full transition-colors">
              <XCircle className="text-slate-400 hover:text-cyan-400" />
            </button>
          </div>

          <div className="space-y-6">
            {item.invitationStatus === 'PENDING' && (
<div className="bg-gradient-to-r from-cyan-400/20 to-blue-500/10 border-l-4 border-cyan-400 p-4 rounded-r-lg shadow-[0_0_15px_rgba(34,211,238,0.05)]">
  <p className="text-sm text-cyan-200">
    <span className="font-black text-cyan-300 underline tracking-wide">
      Message from {item.postedBy}:
    </span>
    <br />
    <span className="italic mt-1 block">"{item.message}"</span>
  </p>
</div>
)}

            {/* Experience & Role Info */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-xl text-center shadow-[0_4px_12px_rgba(99,102,241,0.05)]">
                <div className="text-indigo-400 text-[10px] uppercase font-black tracking-widest mb-1">Role Level</div>
                <div className="font-black text-indigo-200 text-sm">{item.roleLevel}</div>
              </div>
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center shadow-[0_4px_12px_rgba(16,185,129,0.05)]">
                <div className="text-emerald-400 text-[10px] uppercase font-black tracking-widest mb-1">Experience</div>
                <div className="font-black text-emerald-200 text-sm">{item.miniExperience}-{item.maxExperience} Yrs</div>
              </div>
             
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-center shadow-[0_4px_12px_rgba(245,158,11,0.05)]">
                <div className="text-amber-400 text-[10px] uppercase font-black tracking-widest mb-1">Team Size</div>
                <div className="font-black text-amber-200 text-sm">{item.requiredTeamSize}</div>
              </div>
            </div>

            {/* Primary Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-sky-500/10 border border-sky-400/30 rounded-xl shadow-[0_4px_12px_rgba(14,165,233,0.05)]">
                <div className="text-sky-400 text-xs font-bold flex items-center gap-1.5 mb-1"><Clock size={14} className="text-sky-400" /> Notice Period</div>
                <div className="font-black text-sky-100">{item.noticePeriodDays} Days</div>
              </div>
              <div className="p-3 bg-emerald-500/15 border border-emerald-400/40 rounded-xl shadow-[0_4px_15px_rgba(52,211,153,0.1)]">
                <div className="text-emerald-400 text-xs font-bold flex items-center gap-1.5 mb-1"> Compensation</div>
                <div className="font-black text-emerald-300 text-sm">₹{item.compensationOffer?.toLocaleString()} Laks</div>
              </div>
              {/* <div className="p-3 bg-purple-500/10 border border-purple-400/30 rounded-xl shadow-[0_4px_12px_rgba(168,85,247,0.05)]">
                <div className="text-purple-400 text-xs font-bold flex items-center gap-1.5 mb-1"><MapPin size={14} className="text-purple-400" /> {item.workType}</div>
                <div className="font-black text-purple-100 truncate">{item.location}</div>
              </div> */}

                <div className="p-3 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-xl text-center shadow-[0_4px_12px_rgba(217,70,239,0.05)]">
                <div className="text-fuchsia-400 text-[10px] uppercase font-black tracking-widest mb-1">Min Budget Handled</div>
                <div className="font-black text-fuchsia-200 text-sm">{item.minBudgetHandled}</div>
              </div>

            </div>

            {/* Domain & Budget */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-b border-slate-800/60 py-4">
              <div className="flex flex-col bg-slate-950/40 p-2.5 rounded-lg border border-slate-800">
                <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Target Domain</span>
                {/* <span className="text-sm font-bold text-cyan-300 mt-0.5">{item.requiredDomain}</span> */}
                {item.requiredDomain?.map((skill, idx) => (
                    <span key={idx} className="text-sm font-bold text-cyan-300 mt-0.5">
                      {skill}
                    </span>
                  ))}

                
              </div>
              <div className="flex flex-col bg-slate-950/40 p-2.5 rounded-lg border border-slate-800">
                <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Ownership</span>
                {/* <span className="text-sm font-bold text-amber-300 mt-0.5">{item.requiredOwnershipLevel}</span> */}
                {item.requiredOwnershipLevel?.map((skill, idx) => (
                    <span key={idx} className="text-sm font-bold text-amber-300 mt-0.5">
                      {skill}
                    </span>
                  ))}
              </div>
              
            </div>

            {/* Impact & Tech Skills */}
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-black text-slate-400 mb-2 uppercase tracking-widest">Technical Arsenal</h4>
                <div className="flex flex-wrap gap-2">
                  {item.requiredTechSkills?.map((skill, idx) => (
                    <span key={idx} className="px-3 py-1 bg-fuchsia-400 text-slate-950 rounded-md text-xs font-black shadow-[0_0_12px_rgba(232,121,249,0.2)] tracking-wide">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-black text-slate-400 mb-2 uppercase tracking-widest">Expected Impact</h4>
                <div className="flex flex-wrap gap-2">
                  {item.expectedImpactLevel?.map((impact, idx) => (
                    <span key={idx} className="px-3 py-1 bg-amber-400 text-slate-950 rounded-md text-xs font-bold shadow-[0_0_12px_rgba(251,191,36,0.2)] tracking-wide">
                      {impact}
                    </span>
                  ))}
                </div>
              </div>
              <div>
  {/* Section Header */}

  <div>
  {/* Section Header */}
  <h4 className="text-xs font-black tracking-widest uppercase text-slate-400 mb-2">
    Job Location
  </h4>
  
  {/* Combined Work Type & Location Badges Line */}
  <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-purple-400">
    <div className="flex items-center gap-1.5">
      <MapPin size={14} className="text-purple-400" />
      <span>{item.workType}</span>
    </div>
    
    {/* Divider (Optional: only shows if locations exist) */}
    {item.location?.length > 0 && <span className="text-slate-600">•</span>}

    <div className="flex flex-wrap gap-2">
      {item.location?.map((loc, idx) => (
        <span 
          key={idx} 
          className="px-2.5 py-1 text-xs font-bold tracking-wide rounded-md bg-cyan-400 text-slate-950 shadow-[0_0_12px_rgba(34,211,238,0.2)]"
        >
          {loc}
        </span>
      ))}
    </div>
  </div>
</div>
  
 
</div>


            </div>

            {/* Description/Message */}
            {/* <div className="bg-slate-950 border-l-4 border-violet-400 p-4 rounded-r-lg shadow-inner">
              <p className="text-sm text-slate-300">
                <span className="font-black underline text-violet-400 tracking-wide">Job Description from {item.postedBy}:</span>
                <br />
                <span className="italic mt-1 block">"{item.jobDescription}"</span>
              </p>
            </div> */}


<div className="bg-slate-950 border-l-4 border-violet-400 rounded-r-lg p-4 shadow-inner">
  <div className="text-sm text-slate-300">
    <span className="text-xs font-black tracking-widest uppercase text-violet-400 block mb-3">
      Job Description from {item.postedBy}:
    </span>
    
    <div className="text-slate-300 leading-relaxed text-sm space-y-3">
      {(() => {
        let inCodeBlock = false;
        let codeContent: string[] = [];
        const renderedElements: React.ReactNode[] = [];

        // Split text by lines
        const lines = item.jobDescription?.split('\n') || [];

        // Helper function to turn **text** into <strong> and *text* into <em> dynamically
        const renderFormattedText = (text: string) => {
          const tokens = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
          return tokens.map((token: string, tIdx: number) => {
            if (token.startsWith('**') && token.endsWith('**')) {
              return (
                <strong key={tIdx} className="font-extrabold text-slate-100 mx-0.5">
                  {token.slice(2, -2)}
                </strong>
              );
            }
            if (token.startsWith('*') && token.endsWith('*')) {
              return (
                <em key={tIdx} className="italic text-violet-300 mx-0.5">
                  {token.slice(1, -1)}
                </em>
              );
            }
            return token;
          });
        };

        lines.forEach((line: string, index: number) => {
          const trimmedLine = line.trim();

          // 1. Handle Code Block Toggles (```)
          if (trimmedLine.startsWith('```')) {
            if (inCodeBlock) {
              // Closing code block -> render accumulated content
              renderedElements.push(
                <pre key={`code-${index}`} className="bg-slate-900 border border-slate-800 rounded-lg p-3 font-mono text-xs text-pink-400 overflow-x-auto my-2 block select-all">
                  <code>{codeContent.join('\n')}</code>
                </pre>
              );
              codeContent = [];
              inCodeBlock = false;
            } else {
              // Opening code block
              inCodeBlock = true;
            }
            return;
          }

          // If inside a code block, accumulate lines without parsing formatting tags
          if (inCodeBlock) {
            codeContent.push(line);
            return;
          }

          if (!trimmedLine) return;

          // 2. Handle Headings (# Heading)
          if (trimmedLine.startsWith('#')) {
            const headingText = trimmedLine.replace(/^#+\s*/, '');
            renderedElements.push(
              <h3 key={index} className="text-base font-bold text-slate-100 mt-4 mb-1">
                {renderFormattedText(headingText)}
              </h3>
            );
            return;
          }

          // 3. Handle Bullet Points (• or -)
          const isBullet = trimmedLine.startsWith('•') || trimmedLine.startsWith('-');
          if (isBullet) {
            const bulletText = trimmedLine.replace(/^[•-]\s*/, '');
            renderedElements.push(
              <ul key={index} className="list-disc pl-5 my-0.5 text-slate-300">
                <li>{renderFormattedText(bulletText)}</li>
              </ul>
            );
            return;
          }

          // 4. Fallback Standard Paragraph
          renderedElements.push(
            <p key={index} className="text-slate-300">
              {renderFormattedText(trimmedLine)}
            </p>
          );
        });

        // Safety fallback if code block wasn't closed properly
        if (inCodeBlock && codeContent.length > 0) {
          renderedElements.push(
            <pre key="code-unclosed" className="bg-slate-900 border border-slate-800 rounded-lg p-3 font-mono text-xs text-pink-400 overflow-x-auto my-2 block">
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
        }

        return renderedElements;
      })()}
    </div>
  </div>
</div>





            {/* Footer Timestamps */}
            <div className="flex justify-between text-[10px] text-slate-500 font-bold border-t border-slate-800 pt-4">
              <span>Created: {new Date(item.jobCreatedAt).toLocaleDateString()}</span>
              <span>Last Updated: {new Date(item.jobUpdatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </Dialog.Panel>
      </Transition.Child>
    </div>
  </div>
</Dialog>
</Transition>

    {/* --- MODAL 2: Action (Accept/Reject) --- */}
    <Transition show={isActionOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setIsActionOpen(false)}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-6">
              <Dialog.Title className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
                <Send className="text-cyan-400" size={20} /> Finalize Your Response
              </Dialog.Title>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-400 mb-1">Status</label>
                  <select 
                    value={response.status}
                    onChange={(e) => setResponse({...response, status: e.target.value})}
                    className="w-full bg-slate-950 text-slate-200 border-2 border-slate-800 rounded-xl p-3 focus:border-cyan-500 focus:ring-0 transition"
                  >
                    <option value="ACCEPTED">✅ Accept Invitation</option>
                    <option value="REJECTED">❌ Decline Invitation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-400 mb-1">Personal Note</label>
                  <textarea 
                    value={response.message}
                    onChange={(e) => setResponse({...response, message: e.target.value})}
                    placeholder="Add a message for the hiring manager..."
                    className="w-full bg-slate-950 text-slate-200 border-2 border-slate-800 rounded-xl p-3 h-32 focus:border-cyan-500 focus:ring-0 transition placeholder-slate-600"
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <button 
                    onClick={() => setIsActionOpen(false)}
                    className="flex-1 px-4 py-3 rounded-xl font-bold text-slate-400 hover:bg-slate-800 transition"
                  >
                    Go Back
                  </button>
                  <button 
                  onClick={handleSubmit}
                    className={`flex-1 px-4 py-3 rounded-xl font-bold text-slate-950 shadow-lg transition transform active:scale-95 ${
                      response.status === 'ACCEPT' ? 'bg-cyan-400 hover:bg-cyan-300' : 'bg-rose-400 hover:bg-rose-300'
                    }`}
                  >
                    Confirm Action
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  </div>
))};
</>
)
};

export default JobInvitationComponent;